"use client";

import type { ReactNode, FC, Ref } from "react";
import {
  Container,
  type ContainerProps,
  Heading,
  type ClassNameProp,
  Card,
  Text,
  ValueParser,
  Flex,
  mergeRefs,
  Chip,
  Placeholder,
  Icon,
  Spacing,
  Timeline,
  type TimelineRecord,
  ScrollPane,
} from "@huub-web/ui";
import { mergeClasses, padNumber, rem } from "@huub-web/utils";

import { Section } from "../Section";
import { useContentMenuSection } from "../../hooks/useContentMenuSection";
import { Triptych } from "../Triptych";
import { InfoIcon } from "../InfoIcon";
import { AnalysisIndicator } from "../AnalysisIndicator";
import { Tip } from "../Tip";
import { SummaryLoading } from "../Analysis/components/SummaryLoading";
import {
  analysisSectionIllustrations,
  type AnalysisSectionIndex,
} from "../Analysis/types/AnalysisSections";

import { timelineScrollPaneStyle } from "./PropertyValueAnalysis.css";

type WOZRecord = { year: number; price: number; percentage: number };

export type PropertyValueAnalysisProps = ClassNameProp & {
  /**
   * Index of the section
   **/
  index?: AnalysisSectionIndex;
  /**
   * Container size
   **/
  container?: ContainerProps["size"];
  /**
   * Current price of the property
   **/
  currentPrice?: number;
  /**
   * Square meter price of the property
   **/
  squareMeterPrice?: number;
  /**
   * WOZ prices of the property
   **/
  wozPrices?: WOZRecord[];
  /**
   * Description of the property value
   **/
  description?: string;
  /**
   * Realistic offer of the property
   **/
  realisticOffer?: ReactNode;
  /**
   * Whether the component is loading
   **/
  loading?: boolean;
  /**
   * Whether the component has no data
   **/
  noData?: boolean;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * PropertyValueAnalysis component displays property value information
 * with various data points in a structured format
 */
export const PropertyValueAnalysis: FC<PropertyValueAnalysisProps> = ({
  index,
  container = "marketing",
  currentPrice,
  squareMeterPrice,
  wozPrices,
  description,
  realisticOffer,
  loading,
  noData,
  className,
  ref,
}) => {
  const sectionRef = useContentMenuSection<HTMLDivElement>({
    id: "property-value",
    label: "Woningwaarde",
    index,
  });

  const id = `analysisSection${index}`;

  // const lastWOZ = useMemo<WOZRecord | undefined>(() => {
  //   if (!wozPrices || wozPrices.length === 0) {
  //     return undefined;
  //   }
  //   return wozPrices[wozPrices.length - 1];
  // }, [wozPrices]);

  // const graphData = useMemo<GraphProps["data"]>(() => {
  //   if (!wozPrices || wozPrices.length === 0) {
  //     return [];
  //   }

  //   return wozPrices.map((item) => ({
  //     label: item.year.toString(),
  //     tooltip: (
  //       <Flex key={item.year} gap="s" align="flex-end">
  //         <Text variant="bodyL" bold>
  //           <ValueParser value={{ type: "money", value: item.price }} />
  //         </Text>
  //         <Text
  //           variant="bodyM"
  //           bold
  //           color={
  //             item.percentage === 0
  //               ? "currentColor50"
  //               : item.percentage > 0
  //                 ? "success"
  //                 : "error"
  //           }
  //         >
  //           <ValueParser
  //             value={{
  //               type: "percentageWithPlus",
  //               value: item.percentage,
  //             }}
  //           />
  //         </Text>
  //       </Flex>
  //     ),
  //     woz: item.price,
  //   }));
  // }, [wozPrices]);

  // const yAxisDomain = useMemo((): [number, number] | undefined => {
  //   if (!wozPrices || wozPrices.length === 0) {
  //     return undefined;
  //   }

  //   const prices = wozPrices.map((item) => item.price);
  //   const minPrice = Math.min(...prices);
  //   const maxPrice = Math.max(...prices);

  //   // Add 10% padding on each side
  //   const range = maxPrice - minPrice;
  //   const padding = range * 0.1;

  //   // Round to nearest 10k for cleaner axis values
  //   const roundToNearest = 10000;
  //   const min =
  //     Math.floor((minPrice - padding) / roundToNearest) * roundToNearest;
  //   // const max =
  //   //   Math.ceil((maxPrice + padding) / roundToNearest) * roundToNearest;

  //   return [min, maxPrice];
  // }, [wozPrices]);

  const wozTimelineRecords: TimelineRecord[] = (() => {
    if (!wozPrices || wozPrices.length === 0) {
      return [];
    }
    return wozPrices
      .sort((a, b) => b.year - a.year)
      .map((item) => ({
        title: <ValueParser value={{ type: "money", value: item.price }} />,
        subtitle: item.year.toString(),
        percentage: item.percentage,
      }));
  })();

  return (
    <Section
      id={id}
      ref={mergeRefs(ref, sectionRef)}
      className={mergeClasses(className)}
      variant="tint"
    >
      <Container size={container}>
        {index !== undefined && (
          <AnalysisIndicator
            index={index}
            illustration={analysisSectionIllustrations[index]}
          />
        )}
        <Heading
          title="Ontwikkeling woningwaarde"
          description={loading || noData ? <SummaryLoading /> : description}
          variant="main"
        />
        {!noData && (
          <Triptych>
            <Card gap="s" border="default">
              <Flex gap="xs" justify="space-between">
                <Flex align="center" gap="xs">
                  <Text as="h4" bold>
                    WOZ-waardeverloop
                  </Text>
                  <InfoIcon value="De WOZ-waarde is een schatting van de gemeente en geeft een globaal beeld van de woningwaarde — handig als referentie, maar niet altijd gelijk aan de echte marktprijs." />
                </Flex>
                {loading && (
                  <Icon icon="loading" size="s" animation="rotation" />
                )}
              </Flex>
              <Text color="currentColor50">
                Bekijk de ontwikkeling van de woningwaarde over de afgelopen
                jaren.
              </Text>
              <Spacing />
              {loading ? (
                <Flex gap="s" wrap>
                  <Placeholder height={rem(24)} width="90%" variant="loading" />
                  <Placeholder height={rem(24)} width="70%" variant="loading" />
                  <Placeholder height={rem(24)} width="80%" variant="loading" />
                </Flex>
              ) : (
                <ScrollPane className={timelineScrollPaneStyle}>
                  <Timeline records={wozTimelineRecords} />
                </ScrollPane>
              )}

              {/* {lastWOZ && (
                <Flex gap="s" align="flex-end">
                  <Text variant="displayS" bold>
                    <ValueParser
                      value={{ type: "money", value: lastWOZ.price }}
                    />
                  </Text>
                  <Text
                    variant="bodyM"
                    bold
                    color={
                      lastWOZ.percentage === 0
                        ? "currentColor50"
                        : lastWOZ.percentage > 0
                          ? "success"
                          : "error"
                    }
                  >
                    <ValueParser
                      value={{
                        type: "percentageWithPlus",
                        value: lastWOZ.percentage,
                      }}
                    />
                  </Text>
                  {lastWOZ.year && (
                    <Text color="currentColor50">{lastWOZ.year}</Text>
                  )}
                </Flex>
              )}

              {graphData.length > 0 && (
                <>
                  <Spacing />
                  <Graph
                    type="lineChartPlain"
                    data={graphData}
                    valueFormat="money"
                    entries={{ woz: { label: "WOZ-waarde" } }}
                    yAxisDomain={yAxisDomain}
                    className={graphStyle}
                  />
                </>
              )} */}
            </Card>

            <Card border="default">
              <Text as="h3" bold>
                Huidige vraagprijs
              </Text>
              {!!currentPrice && currentPrice > 0 ? (
                <Flex direction="row" gap="s" align="baseline">
                  {!!currentPrice && (
                    <Text variant="displayM" bold>
                      <ValueParser
                        value={{ type: "money", value: currentPrice }}
                      />
                    </Text>
                  )}
                  {!!squareMeterPrice && (
                    <Text color="currentColor50">
                      <ValueParser
                        value={{ type: "money", value: squareMeterPrice }}
                      />
                      /m²
                    </Text>
                  )}
                  {loading && !squareMeterPrice && (
                    <Placeholder height={rem(22)} width={rem(80)} />
                  )}
                </Flex>
              ) : (
                <Chip variant="secondary" alignSelf="flex-start">
                  Onbekend
                </Chip>
              )}
            </Card>

            <Card border="default">
              <Text as="h4" bold>
                Realistisch bod
              </Text>
              {realisticOffer}
            </Card>
          </Triptych>
        )}
      </Container>

      {!loading && (
        <Container size={container} gap="s">
          <Tip name="Tip">
            Laat een bouwkundige keuring uitvoeren door een aan een
            brancheorganisatie aangesloten specialist, zoals het NHG,
            Vastgoedpro, NVB, of VBO
          </Tip>
          <Tip name="Tip">
            Laat een taxatierapport uitvoeren door een aan een
            brancheorganisatie aangesloten specialist, zoals het NWWI
          </Tip>
        </Container>
      )}

      {index !== undefined && (
        <Text align="center">
          {padNumber(index)} &ndash; Ontwikkeling woningwaarde
        </Text>
      )}
    </Section>
  );
};
